import { ICompileService, IPromise, IQService, Injectable } from "angular";
import {
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../records/grids/columns/columnDefinitionSetService";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../records/views/recordGridView";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
  IGridSettings,
} from "../../records/grids/settings/gridSettingsService";
import { StateService } from "@uirouter/angularjs";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../records/grids/columns/filters/FilterProvider";
import { ColDef } from "../../records/grids/columns/colDef";
import { RecordDataSource } from "../../records/grids";
import { moneyValueGetterFactory } from "../../records/grids/cells/valueGetters";
import { OptionsFilter } from "../../records/grids/columns/filters/subFilters/OptionsFilter";
import { get } from "lodash";

export const vsObjectName = "contract";

contractConfigurationConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];

export function contractConfigurationConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    "$q",
    ServerUtilityName,
    GridSettingsServiceName,
    "$state",
    (
      $q: IQService,
      serverUtility: ServerUtility,
      gridSettingsService: GridSettingsService,
      $state: StateService
    ) =>
      new ContractGridConfiguration(
        $q,
        serverUtility,
        gridSettingsService,
        $state
      ),
  ]);

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    "$compile",
    (
      columnDefinitionService: ColumnDefinitionService,
      $compile: ICompileService
    ) => [
      columnDefinitionService.getNumberColDef({
        field: "Id",
        filter: new FilterProvider("Id", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        hide: true,
      }),
      ...contractConfigurationColumns(columnDefinitionService, $compile),
    ],
  ]);
}

function contractConfigurationColumns(
  columnDefinitionService: ColumnDefinitionService,
  $compile: ICompileService
): ColDef[] {
  return [
    columnDefinitionService.getStringColDef({
      field: "ContractMachineManufacturerName",
      header: "Contract Manufacturer",
    }),
    columnDefinitionService.getStringColDef({
      field: "ContractNumber",
    }),
    columnDefinitionService.getStringColDef({
      field: "Cbr",
      header: "Cbr#",
    }),
    columnDefinitionService.getStringColDef({
      field: "Requisition",
      header: "Requisition#",
      hide: true,
    }),
    columnDefinitionService.getStringColDef({
      field: "PO",
      header: "PO#",
      hide: true,
    }),
    columnDefinitionService.getDateColDef({
      field: "ContractStartDate",
      header: "Contract Start",
    }),
    columnDefinitionService.getDateColDef({
      field: "ContractEndDate",
      header: "Contract End",
    }),
    columnDefinitionService.getStringColDef({
      field: "TypeOfAgreementName",
      header: "Type Of Agreement ",
    }),
    columnDefinitionService.getNumberColDef({
      field: "ContractValue",
      header: "Contract Value",
      valueGetter: moneyValueGetterFactory(),
      hide: true,
      requiresPermission: "contractViewSensitive",
    }),
    columnDefinitionService.getStringColDef({
      field: "OwnedLeased",
      header: "Owned/Leased",
      hide: true,
      filter: new FilterProvider("OwnedLeased", $compile)
        .addFilter(
          "=",
          new OptionsFilter({
            displayName: "=",
            options: [
              {
                display: "Leased",
                value: "Leased",
              },
              {
                display: "Owned",
                value: "Owned",
              },
            ],
            queryFieldNames: ["OwnedLeased"],
          })
        )
        .getGridFilterFactory(),
      requiresPermission: "contractViewSensitive",
    }),
    columnDefinitionService.getStringColDef({
      field: "PerformanceGuaranteeTypeName",
      header: "Performance Guarantee",
      hide: true,
      requiresPermission: "contractViewSensitive",
    }),
    columnDefinitionService.getStringColDef({
      field: "Details",
      header: "Details",
      requiresPermission: "contractViewSensitive",
      hide: true,
    }),
    columnDefinitionService.getStringColDef({
      field: "FeeStructureTypeName",
      header: "Fee Structure",
      requiresPermission: "contractViewSensitive",
      hide: true,
    }),
    columnDefinitionService.getStringColDef({
      field: "Notes",
      header: "Notes",
      hide: true,
    }),
    columnDefinitionService.getStringColDef({
      field: "ExecutedContract",
      header: "Executed Contract",
      hide: true,
      requiresPermission: "contractViewSensitive",
      valueGetter: (params) => get(params.data, "ExecutedContract.Name"),
      cellRenderer: (params) => {
        const executedContract = get(params.data, "ExecutedContract");
        if (!executedContract) {
          return "";
        }

        return `<a href="${executedContract.Url}" target="_blank">${executedContract.Name}</a>`;
      },
    }),
  ];
}

class ContractGridConfiguration implements GridConfiguration {
  columns?: ColDef[];
  columnDefinitionSet?: string = vsObjectName;
  onItemCommand?: Injectable<(...args: any[]) => IPromise<void>> = [
    () => {
      return this.$q.when();
    },
  ];
  gridDataSource?: RecordDataSource = new RecordDataSource(
    this.serverUtility,
    "/api/contract/contract"
  );
  defaultSort?: string = "Id";
  nameSingular?: string = "Contract Configuration";
  namePlural?: string = "Contract Configurations";
  showAddButton?: boolean = true;
  showAddButtonPermission?: string = "contractEdit";
  gridSelectionChanged?: Injectable<(...args: any[]) => void> = [
    () => {
      return this.$q.when();
    },
  ];
  showSelectButtons?: boolean = true;
  gridSettings?: IGridSettings;

  static $inject = ["$q", ServerUtilityName, GridSettingsServiceName, "$state"];
  constructor(
    private readonly $q: IQService,
    private readonly serverUtility: ServerUtility,
    gridSettingsService: GridSettingsService,
    private readonly $state: StateService
  ) {
    this.gridSettings = gridSettingsService.getGridSettings(
      Symbol("main.contracts.grid.2")
    );
  }

  onAddNew = () => {
    return this.$state.go("^.add").then(() => {});
  };
}
